import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  makeStyles,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import { useTranslation } from "react-i18next";

import AppButton from "components/CustomButtons/AppButton";
import AppDialog from "components/Dialog/AppDialog";
import AppColor from "utilities/AppColor";
import { getAuthInfo } from "store/slices/auth";
import {
  deactivateMedication,
  fetchAllMedications,
  getMedications,
  getMedicationsLoading,
  saveMedication,
} from "store/slices/medication";

// ── constants ──────────────────────────────────────────────────────────────

const ROUTES = ["Oral", "Topical", "Inhaled", "Injection", "Other"];

const FREQUENCIES = [
  { value: "ONCE_DAILY", label: "Once daily", times: 1 },
  { value: "TWICE_DAILY", label: "Twice daily", times: 2 },
  { value: "THREE_TIMES_DAILY", label: "Three times daily", times: 3 },
  { value: "FOUR_TIMES_DAILY", label: "Four times daily", times: 4 },
  { value: "PRN", label: "As needed (PRN)", times: 0 },
  { value: "WEEKLY", label: "Weekly", times: 1 },
  { value: "OTHER", label: "Other", times: 0 },
];

const ROUTE_COLORS = {
  Oral: { bg: "#DBEAFE", color: "#1D4ED8" },
  Topical: { bg: "#D1FAE5", color: "#065F46" },
  Inhaled: { bg: "#EDE9FE", color: "#5B21B6" },
  Injection: { bg: "#FEE2E2", color: "#991B1B" },
  Other: { bg: "#F3F4F6", color: "#374151" },
};

const FREQ_LABELS = Object.fromEntries(FREQUENCIES.map((f) => [f.value, f.label]));

const emptyForm = {
  id: null,
  name: "",
  genericName: "",
  dose: "",
  route: "Oral",
  frequency: "ONCE_DAILY",
  scheduledTimes: ["07:00"],
  withFood: false,
  prescribingDoctorName: "",
  pharmacyName: "",
  storageLocation: "",
  indication: "",
  prnConditions: "",
  prnRequiresSeniorApproval: false,
  specialInstructions: "",
  startDate: "",
  reviewDate: "",
};

// ── styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles(() => ({
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  title: { fontWeight: 700, fontSize: 18, color: "#111827" },
  card: {
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    padding: "14px 16px",
    marginBottom: 12,
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
  },
  cardLeft: { flex: 1 },
  medName: { fontWeight: 700, fontSize: 15, color: "#111827" },
  medGeneric: { fontSize: 13, color: "#6B7280", marginLeft: 6 },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 6,
    alignItems: "center",
  },
  chip: { height: 22, fontSize: 12 },
  metaText: { fontSize: 12, color: "#6B7280" },
  infoRow: { marginTop: 6 },
  infoLabel: { fontSize: 12, color: "#9CA3AF", marginRight: 4 },
  infoValue: { fontSize: 12, color: "#374151" },
  cardActions: { display: "flex", gap: 4, flexShrink: 0 },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 4,
    marginTop: 12,
  },
  fieldError: { fontSize: 11, color: "#EF4444", marginTop: 2 },
  toggleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 0",
    borderBottom: "1px solid #F3F4F6",
  },
  emptyBox: {
    padding: "40px 0",
    textAlign: "center",
    color: "#9CA3AF",
    fontSize: 14,
  },
  timeInput: {
    marginRight: 8,
    marginBottom: 4,
    "& input": { padding: "6px 8px", fontSize: 13 },
  },
  select: {
    width: "100%",
    padding: "8px 10px",
    border: "1px solid #D1D5DB",
    borderRadius: 6,
    fontSize: 14,
    background: "#fff",
    color: "#111827",
    outline: "none",
    "&:focus": { borderColor: AppColor.mainColor },
  },
}));

// ── helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-AU", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const toApiDate = (v) => {
  if (!v) return "";
  return v.replace(/-/g, "/");
};

// ── FormField helper ─────────────────────────────────────────────────────────

const FormField = ({ label, required, error, children }) => {
  const classes = useStyles();
  return (
    <Box mb={1.5}>
      <Typography className={classes.sectionLabel}>
        {label}{required && " *"}
      </Typography>
      {children}
      {error && <div className={classes.fieldError}>{error}</div>}
    </Box>
  );
};

// ── main component ────────────────────────────────────────────────────────────

const ParticipantMedication = ({ companyId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const { id: clientId } = useParams();
  const authInfo = useSelector(getAuthInfo);
  const medications = useSelector(getMedications);
  const loading = useSelector(getMedicationsLoading);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // medication object
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (clientId) dispatch(fetchAllMedications(clientId));
  }, [clientId]);

  const openAdd = () => {
    setForm(emptyForm);
    setErrors({});
    setOpen(true);
  };

  const openEdit = (med) => {
    const times = med.scheduledTimes
      ? med.scheduledTimes.split(",").map((s) => s.trim()).filter(Boolean)
      : ["07:00"];
    setForm({
      id: med.id,
      name: med.name || "",
      genericName: med.genericName || "",
      dose: med.dose || "",
      route: med.route || "Oral",
      frequency: med.frequency || "ONCE_DAILY",
      scheduledTimes: times,
      withFood: med.withFood || false,
      prescribingDoctorName: med.prescribingDoctorName || "",
      pharmacyName: med.pharmacyName || "",
      storageLocation: med.storageLocation || "",
      indication: med.indication || "",
      prnConditions: med.prnConditions || "",
      prnRequiresSeniorApproval: med.prnRequiresSeniorApproval || false,
      specialInstructions: med.specialInstructions || "",
      startDate: med.startDate ? med.startDate.substring(0, 10) : "",
      reviewDate: med.reviewDate ? med.reviewDate.substring(0, 10) : "",
    });
    setErrors({});
    setOpen(true);
  };

  const set = (key) => (e) => {
    const value = e && e.target !== undefined ? e.target.value : e;
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const setToggle = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.checked }));
  };

  const handleFrequencyChange = (e) => {
    const freq = e.target.value;
    const meta = FREQUENCIES.find((f) => f.value === freq);
    const count = meta ? meta.times : 1;
    let times = [...(form.scheduledTimes || [])];
    if (count > 0) {
      while (times.length < count) times.push("08:00");
      times = times.slice(0, count);
    }
    setForm((prev) => ({ ...prev, frequency: freq, scheduledTimes: times }));
  };

  const handleTimeChange = (idx, value) => {
    const times = [...form.scheduledTimes];
    times[idx] = value;
    setForm((prev) => ({ ...prev, scheduledTimes: times }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t("validation.required") || "Required";
    if (!form.dose.trim()) e.dose = t("validation.required") || "Required";
    if (!form.route) e.route = t("validation.required") || "Required";
    if (!form.frequency) e.frequency = t("validation.required") || "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    const payload = {
      ...form,
      clientId: Number(clientId),
      companyId: Number(companyId || authInfo.companyId),
      startDate: toApiDate(form.startDate),
      reviewDate: toApiDate(form.reviewDate),
    };
    const result = await dispatch(saveMedication(payload));
    setSaving(false);
    if (result.payload) {
      setOpen(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    await dispatch(deactivateMedication(confirmDelete.id));
    setConfirmDelete(null);
  };

  const isPrn = form.frequency === "PRN";
  const freqMeta = FREQUENCIES.find((f) => f.value === form.frequency);
  const showTimePickers = freqMeta && freqMeta.times > 0;

  return (
    <Box p={2}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography className={classes.title}>{t("label.medication")}</Typography>
        <AppButton
          label={t("label.add_medication")}
          startIcon={<AddIcon />}
          buttonColor={AppColor.mainColor}
          onClick={openAdd}
        />
      </Box>

      {/* List */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} style={{ color: AppColor.mainColor }} />
        </Box>
      ) : medications.length === 0 ? (
        <Box className={classes.emptyBox}>{t("label.no_medications")}</Box>
      ) : (
        medications.map((med) => {
          const routeColor = ROUTE_COLORS[med.route] || ROUTE_COLORS.Other;
          const times = med.scheduledTimes
            ? med.scheduledTimes.split(",").map((s) => s.trim()).filter(Boolean)
            : [];
          return (
            <Box key={med.id} className={classes.card}>
              <Box className={classes.cardLeft}>
                <Box display="flex" alignItems="center" flexWrap="wrap">
                  <Typography className={classes.medName}>{med.name}</Typography>
                  {med.genericName && (
                    <Typography className={classes.medGeneric}>({med.genericName})</Typography>
                  )}
                </Box>

                <Box className={classes.metaRow}>
                  <Chip
                    label={med.dose}
                    size="small"
                    className={classes.chip}
                    style={{ background: "#F3F4F6", color: "#374151", fontWeight: 600 }}
                  />
                  <Chip
                    label={med.route}
                    size="small"
                    className={classes.chip}
                    style={{ background: routeColor.bg, color: routeColor.color }}
                  />
                  <Chip
                    label={FREQ_LABELS[med.frequency] || med.frequency}
                    size="small"
                    className={classes.chip}
                    style={{ background: "#EDE9FE", color: "#5B21B6" }}
                  />
                  {med.frequency === "PRN" && (
                    <Chip
                      label="PRN"
                      size="small"
                      className={classes.chip}
                      style={{ background: "#FEF3C7", color: "#92400E", fontWeight: 700 }}
                    />
                  )}
                  {med.withFood && (
                    <Chip
                      label="With food"
                      size="small"
                      className={classes.chip}
                      style={{ background: "#D1FAE5", color: "#065F46" }}
                    />
                  )}
                </Box>

                {times.length > 0 && (
                  <Box className={classes.metaRow}>
                    <Typography className={classes.metaText}>Times:</Typography>
                    {times.map((t, i) => (
                      <Chip key={i} label={t} size="small" className={classes.chip} style={{ background: "#F9FAFB" }} />
                    ))}
                  </Box>
                )}

                <Box className={classes.infoRow} display="flex" flexWrap="wrap" style={{ gap: 16 }}>
                  {med.prescribingDoctorName && (
                    <Typography>
                      <span className={classes.infoLabel}>Dr:</span>
                      <span className={classes.infoValue}>{med.prescribingDoctorName}</span>
                    </Typography>
                  )}
                  {med.pharmacyName && (
                    <Typography>
                      <span className={classes.infoLabel}>Pharmacy:</span>
                      <span className={classes.infoValue}>{med.pharmacyName}</span>
                    </Typography>
                  )}
                  {med.storageLocation && (
                    <Typography>
                      <span className={classes.infoLabel}>Storage:</span>
                      <span className={classes.infoValue}>{med.storageLocation}</span>
                    </Typography>
                  )}
                  {med.startDate && (
                    <Typography>
                      <span className={classes.infoLabel}>Start:</span>
                      <span className={classes.infoValue}>{formatDate(med.startDate)}</span>
                    </Typography>
                  )}
                  {med.reviewDate && (
                    <Typography>
                      <span className={classes.infoLabel}>Review:</span>
                      <span className={classes.infoValue}>{formatDate(med.reviewDate)}</span>
                    </Typography>
                  )}
                </Box>

                {med.indication && (
                  <Typography className={classes.infoRow}>
                    <span className={classes.infoLabel}>Indication:</span>
                    <span className={classes.infoValue}>{med.indication}</span>
                  </Typography>
                )}
                {med.specialInstructions && (
                  <Typography className={classes.infoRow}>
                    <span className={classes.infoLabel}>Instructions:</span>
                    <span className={classes.infoValue}>{med.specialInstructions}</span>
                  </Typography>
                )}
                {med.frequency === "PRN" && med.prnConditions && (
                  <Typography className={classes.infoRow}>
                    <span className={classes.infoLabel}>PRN when:</span>
                    <span className={classes.infoValue}>{med.prnConditions}</span>
                  </Typography>
                )}
              </Box>

              <Box className={classes.cardActions}>
                <Tooltip title={t("label.edit")}>
                  <IconButton size="small" onClick={() => openEdit(med)}>
                    <EditIcon fontSize="small" style={{ color: AppColor.mainColor }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("label.remove_medication")}>
                  <IconButton size="small" onClick={() => setConfirmDelete(med)}>
                    <DeleteIcon fontSize="small" style={{ color: "#EF4444" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          );
        })
      )}

      {/* Add / Edit dialog */}
      <AppDialog
        open={open}
        onClose={() => setOpen(false)}
        title={form.id ? t("label.edit_medication") : t("label.add_medication")}
        maxWidth="sm"
        actions={[
          {
            label: t("button.cancel"),
            onPress: () => setOpen(false),
            buttonColor: "#9CA3AF",
          },
          {
            label: saving ? t("label.loading") : t("button.save"),
            onPress: handleSave,
            buttonColor: AppColor.mainColor,
            disabled: saving,
          },
        ]}
      >
        <Box pt={1}>
          <Grid container spacing={2}>
            {/* Name + Generic name */}
            <Grid item xs={12} sm={7}>
              <FormField label={t("label.medication_name")} required error={errors.name}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={form.name}
                  onChange={set("name")}
                  error={!!errors.name}
                  placeholder="e.g. Metformin"
                />
              </FormField>
            </Grid>
            <Grid item xs={12} sm={5}>
              <FormField label={t("label.generic_name")}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={form.genericName}
                  onChange={set("genericName")}
                  placeholder="e.g. metformin hydrochloride"
                />
              </FormField>
            </Grid>

            {/* Dose + Route */}
            <Grid item xs={12} sm={4}>
              <FormField label={t("label.dose")} required error={errors.dose}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={form.dose}
                  onChange={set("dose")}
                  error={!!errors.dose}
                  placeholder="e.g. 500mg"
                />
              </FormField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormField label={t("label.route")} required error={errors.route}>
                <select
                  className={classes.select}
                  value={form.route}
                  onChange={set("route")}
                >
                  {ROUTES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </FormField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormField label={t("label.frequency")} required error={errors.frequency}>
                <select
                  className={classes.select}
                  value={form.frequency}
                  onChange={handleFrequencyChange}
                >
                  {FREQUENCIES.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </FormField>
            </Grid>

            {/* Scheduled times */}
            {showTimePickers && (
              <Grid item xs={12}>
                <FormField label={t("label.scheduled_times")}>
                  <Box display="flex" flexWrap="wrap">
                    {form.scheduledTimes.map((time, idx) => (
                      <TextField
                        key={idx}
                        type="time"
                        value={time}
                        onChange={(e) => handleTimeChange(idx, e.target.value)}
                        variant="outlined"
                        size="small"
                        className={classes.timeInput}
                        style={{ width: 130 }}
                      />
                    ))}
                  </Box>
                </FormField>
              </Grid>
            )}

            {/* With food */}
            <Grid item xs={12}>
              <Box className={classes.toggleRow}>
                <Typography style={{ fontSize: 14, color: "#374151" }}>{t("label.with_food")}</Typography>
                <Switch
                  checked={form.withFood}
                  onChange={setToggle("withFood")}
                  color="primary"
                  size="small"
                />
              </Box>
            </Grid>

            {/* Doctor + Pharmacy */}
            <Grid item xs={12} sm={6}>
              <FormField label={t("label.prescribing_doctor")}>
                <TextField fullWidth variant="outlined" size="small" value={form.prescribingDoctorName} onChange={set("prescribingDoctorName")} />
              </FormField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormField label={t("label.pharmacy_name")}>
                <TextField fullWidth variant="outlined" size="small" value={form.pharmacyName} onChange={set("pharmacyName")} />
              </FormField>
            </Grid>

            {/* Storage */}
            <Grid item xs={12}>
              <FormField label={t("label.storage_location")}>
                <TextField fullWidth variant="outlined" size="small" value={form.storageLocation} onChange={set("storageLocation")} placeholder="e.g. Locked medication cupboard" />
              </FormField>
            </Grid>

            {/* Indication */}
            <Grid item xs={12}>
              <FormField label={t("label.indication")}>
                <TextField fullWidth variant="outlined" size="small" multiline minRows={2} value={form.indication} onChange={set("indication")} />
              </FormField>
            </Grid>

            {/* PRN-specific fields */}
            {isPrn && (
              <>
                <Grid item xs={12}>
                  <FormField label={t("label.prn_conditions")}>
                    <TextField fullWidth variant="outlined" size="small" multiline minRows={2} value={form.prnConditions} onChange={set("prnConditions")} placeholder="Describe when to administer" />
                  </FormField>
                </Grid>
                <Grid item xs={12}>
                  <Box className={classes.toggleRow}>
                    <Typography style={{ fontSize: 14, color: "#374151" }}>{t("label.prn_requires_senior_approval")}</Typography>
                    <Switch
                      checked={form.prnRequiresSeniorApproval}
                      onChange={setToggle("prnRequiresSeniorApproval")}
                      color="primary"
                      size="small"
                    />
                  </Box>
                </Grid>
              </>
            )}

            {/* Special instructions */}
            <Grid item xs={12}>
              <FormField label={t("label.special_instructions")}>
                <TextField fullWidth variant="outlined" size="small" multiline minRows={2} value={form.specialInstructions} onChange={set("specialInstructions")} />
              </FormField>
            </Grid>

            {/* Start + Review dates */}
            <Grid item xs={12} sm={6}>
              <FormField label={t("label.start_date")}>
                <TextField fullWidth variant="outlined" size="small" type="date" value={form.startDate} onChange={set("startDate")} InputLabelProps={{ shrink: true }} />
              </FormField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormField label={t("label.review_date")}>
                <TextField fullWidth variant="outlined" size="small" type="date" value={form.reviewDate} onChange={set("reviewDate")} InputLabelProps={{ shrink: true }} />
              </FormField>
            </Grid>
          </Grid>
        </Box>
      </AppDialog>

      {/* Confirm delete dialog */}
      <AppDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title={t("label.remove_medication")}
        maxWidth="xs"
        actions={[
          { label: t("button.cancel"), onPress: () => setConfirmDelete(null), buttonColor: "#9CA3AF" },
          { label: t("button.delete"), onPress: handleDelete, buttonColor: "#EF4444" },
        ]}
      >
        <Typography style={{ fontSize: 14, color: "#374151", padding: "8px 0" }}>
          {t("label.remove_medication_confirm")}
          {confirmDelete && (
            <strong> {confirmDelete.name} {confirmDelete.dose}</strong>
          )}
        </Typography>
      </AppDialog>
    </Box>
  );
};

export default ParticipantMedication;
